import {
  CompRef,
  DocumentServicesObject,
  DsItem,
} from '@wix/document-services-types';
import { EditorElement } from '@wix/component-protocol/schema';
import {
  WidgetPointer,
  WidgetSlot,
  WidgetSlotToMoveContent,
} from '@wix/editor-platform-host-integration-apis';
import { find, isEqual } from 'lodash';
import { platformEvents } from '../../../../platformEvents';
import { getPluginInfo, getPluginMarketData } from '../utils';
import {
  VIRTUAL_OOI_SLOT_SEPARATOR,
  SLOT_ID_DIVIDER,
  SUPPORTED_PLUGIN_COMPONENT_TYPES,
} from '../constants';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';
import { createTpaHostAPI } from './tpaHostAPI';
import { createBlocksHostAPI } from './blocksHostAPI';
import { createBuilderHostAPI } from './builderHostAPI';
import { PlatformContext } from '../../../../types/platformApi';
import { createPluginManager } from './pluginManager';
import { PluginActionFullContext, PluginHostApi } from '../types';
import { SlotPointer } from '../slotPointer';

const getHostCompRef = (compRef: CompRef): CompRef => {
  const separator = compRef.id.includes(VIRTUAL_OOI_SLOT_SEPARATOR)
    ? VIRTUAL_OOI_SLOT_SEPARATOR
    : SLOT_ID_DIVIDER;

  return { ...compRef, id: compRef.id.split(separator)[0] };
};

export const createPluginHostApi = (
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
): PluginHostApi => {
  const tpaHostAPI = createTpaHostAPI(documentServicesAPI, experimentsAPI);
  const blocksHostAPI = createBlocksHostAPI(
    documentServicesAPI,
    experimentsAPI,
  );
  const builderHostAPI = createBuilderHostAPI(
    documentServicesAPI,
    experimentsAPI,
  );
  const resolveHostAPIBySlotRef = (slotPlaceholderCompRef: CompRef) => {
    if (slotPlaceholderCompRef.id.includes(VIRTUAL_OOI_SLOT_SEPARATOR)) {
      return tpaHostAPI;
    }
    return blocksHostAPI;
  };
  const resolveHostAPIBySlotPointer = (slotPointer: SlotPointer) => {
    const slotType = slotPointer.getSlotType();
    switch (slotType) {
      case 'OOI':
        return tpaHostAPI;
      case 'Blocks':
        return blocksHostAPI;
      case 'Builder':
        return builderHostAPI;
    }
  };
  return {
    resolveHostAPIBySlotPointer(slotPointer: SlotPointer) {
      return resolveHostAPIBySlotPointer(slotPointer);
    },
    resolveHostAPIByWidgetCompRef(widgetCompRef: CompRef) {
      const type = documentServicesAPI.components.getType(widgetCompRef);
      const builderData =
        documentServicesAPI.builder.components.getCompAppData(type);
      const isBuilder = !!builderData;

      if (isBuilder) {
        return builderHostAPI;
      }

      const isTpa = documentServicesAPI.tpa.isTpaByCompType(
        documentServicesAPI.components.getType(widgetCompRef),
      );
      return isTpa ? tpaHostAPI : blocksHostAPI;
    },
    resolveHostAPIByWidgetPluginRef(widgetPluginCompRef: CompRef) {
      return blocksHostAPI.isPluginComponent(widgetPluginCompRef)
        ? blocksHostAPI
        : tpaHostAPI;
    },
    getWidgetSlot(
      slotPointer: SlotPointer,
      options: { includePrivate: boolean },
    ) {
      return resolveHostAPIBySlotPointer(slotPointer).getWidgetSlot(
        slotPointer,
        options ?? { includePrivate: false }, // TODO, remove fallback when updated editors are GAED
      );
    },
    isSlotPublic(slotCompRef: CompRef) {
      return resolveHostAPIBySlotRef(slotCompRef).isSlotPublic(slotCompRef);
    },
    getWidgetSlots(
      widgetCompRef: CompRef,
      options: { includePrivate: boolean },
    ) {
      return this.resolveHostAPIByWidgetCompRef(widgetCompRef).getWidgetSlots(
        widgetCompRef,
        options,
      );
    },
    getWidgetSlotPlacement(
      slotPointer: SlotPointer,
      { includePrivate }: { includePrivate: boolean },
    ): WidgetSlot['placement'] {
      return resolveHostAPIBySlotPointer(slotPointer).getWidgetSlot(
        slotPointer,
        { includePrivate },
      )?.placement;
    },
    async installWidgetPlugin(
      context: PlatformContext,
      slotPointer: SlotPointer,
      widgetPointer: WidgetPointer,
      actionContext: PluginActionFullContext,
    ): Promise<CompRef> {
      const manager = createPluginManager({
        actionContext,
        platformContext: context,
        hostApiMethods: this,
        documentServicesAPI,
        experimentsAPI,
      });
      const compRef = await manager.add({
        slotPointer,
        widgetPointer,
      });
      return compRef;
    },
    async uninstallWidgetPlugin(
      context: PlatformContext,
      slotPointer: SlotPointer,
      actionContext: PluginActionFullContext,
    ): Promise<void> {
      const manager = createPluginManager({
        actionContext,
        platformContext: context,
        hostApiMethods: this,
        documentServicesAPI,
        experimentsAPI,
      });
      manager.remove({ slotPointer });
    },
    getWidgetPointer(slotPointer: SlotPointer): WidgetPointer | undefined {
      return resolveHostAPIBySlotPointer(slotPointer).getWidgetPointer(
        slotPointer,
      );
    },
    getSlotPlaceholderRefByContentRef(compRef: CompRef): CompRef | undefined {
      if (!this.isPluginComponent(compRef)) {
        return undefined;
      }
      return this.resolveHostAPIByWidgetPluginRef(
        compRef,
      ).getSlotPlaceholderRefByContentRef(compRef);
    },
    getSlotPlaceholderDisplayName(slotCompRef: CompRef): string | undefined {
      return resolveHostAPIBySlotRef(slotCompRef).getSlotPlaceholderDisplayName(
        slotCompRef,
      );
    },
    getWidgetHostRefBySlotRef(slotCompRef: CompRef): CompRef {
      return resolveHostAPIBySlotRef(slotCompRef).getWidgetHostRef(slotCompRef);
    },
    getWidgetHostRefByWidgetRef(compRef: CompRef): CompRef {
      return this.resolveHostAPIByWidgetPluginRef(compRef).getWidgetHostRef(
        compRef,
      );
    },
    getSlotContentRef(slotPointer: SlotPointer): CompRef | undefined {
      return resolveHostAPIBySlotPointer(slotPointer).getSlotContentRef(
        slotPointer,
      );
    },
    getSlotPosition(compRef: CompRef) {
      return resolveHostAPIBySlotRef(compRef).getSlotPosition(compRef);
    },
    getCompRefForSlotSelection(slotPointer: SlotPointer) {
      return resolveHostAPIBySlotPointer(
        slotPointer,
      ).getCompRefForSlotSelection(slotPointer);
    },
    hasWidgetSlot(widgetCompRef: CompRef): boolean {
      return this.resolveHostAPIByWidgetCompRef(widgetCompRef).hasWidgetSlot(
        widgetCompRef,
      );
    },
    getSlotIdInBlocksAppEditor(slotCompRef: CompRef): string {
      return documentServicesAPI.platform.controllers.connections.getPrimaryConnection(
        slotCompRef,
      )?.role;
    },
    getPlacementInBlocksAppEditor(
      slotId: string,
    ): WidgetSlot['placement'] | undefined {
      const widgetPointer =
        documentServicesAPI.appStudio?.widgets.getByRootCompId(
          documentServicesAPI.pages.getCurrentPageId(),
        );
      if (!widgetPointer) {
        return undefined;
      }
      const devCenterWidgetId = documentServicesAPI.data.getById(
        widgetPointer.id,
      )?.devCenterWidgetId;

      return {
        appDefinitionId: window.appStudioModel?.devSiteAppDefId,
        widgetId: devCenterWidgetId,
        slotId,
      };
    },
    getSlotInBlocksAppEditor(slotCompRef: CompRef): WidgetSlot | undefined {
      const slotData =
        documentServicesAPI.components.slots.getSlotsData(slotCompRef).slot;

      const slotId = this.getSlotIdInBlocksAppEditor(slotCompRef);
      const placement = this.getPlacementInBlocksAppEditor(slotId);

      if (!placement) {
        return undefined;
      }

      const slotComponentData: DsItem | undefined = slotData
        ? documentServicesAPI.components.data.get(slotData)
        : undefined;

      return {
        compRef: slotCompRef,
        role: slotId,
        pluginInfo: getPluginInfo(documentServicesAPI, slotComponentData),
        placement,
      };
    },
    getWidgetSlotsToMoveContent(
      slotPointer: SlotPointer,
    ): WidgetSlotToMoveContent[] {
      const hostCompRef =
        resolveHostAPIBySlotPointer(
          slotPointer,
        ).getWidgetHostRefBySlotContainer(slotPointer);
      if (!hostCompRef) {
        return [];
      }

      const widgetPluginPointer = this.getWidgetPointer(slotPointer);
      const widgetSlots = this.getWidgetSlots(hostCompRef, {
        includePrivate: false,
      });
      const marketData = widgetPluginPointer
        ? getPluginMarketData(documentServicesAPI, widgetPluginPointer)
        : undefined;

      const currentPluginPlacements = marketData?.placements ?? [];
      const currentSlotPlacement = this.getWidgetSlotPlacement(slotPointer, {
        includePrivate: false,
      });

      const slotsToMove = widgetSlots.filter((widgetSlot) => {
        return find(currentPluginPlacements, widgetSlot.placement);
      });

      return slotsToMove.map((slot) => ({
        slotRef: slot.compRef,
        pluginInfo: slot.pluginInfo,
        name: this.getSlotPlaceholderDisplayName(slot.compRef),
        isPopulated: this.isWidgetSlotPopulated(
          new SlotPointer({ slotRef: slot.compRef }), // TODO: add support for builder slots later on
        ),
        isSelected: isEqual(currentSlotPlacement, slot.placement),
      }));
    },
    getNearestWidgetHostRef(compRef: CompRef): CompRef | undefined {
      const slots = this.getWidgetSlots(compRef, { includePrivate: false });

      if (slots.length) {
        return compRef;
      }

      const [ancestorRef] =
        documentServicesAPI.components.getAncestors(compRef);

      return getHostCompRef(ancestorRef);
    },
    isSlotContentPotentialMovable(slotPointer: SlotPointer): boolean {
      return (
        this.isWidgetSlotPopulated(slotPointer) &&
        this.getWidgetSlotsToMoveContent(slotPointer).length > 1
      );
    },
    async moveToSlot(
      context: PlatformContext,
      fromSlot: SlotPointer,
      toSlot: SlotPointer,
      actionContext: PluginActionFullContext,
    ) {
      const currentWidgetPluginPointer = this.getWidgetPointer(fromSlot);

      if (!currentWidgetPluginPointer) {
        return;
      }

      await this.installWidgetPlugin(
        context,
        toSlot,
        currentWidgetPluginPointer,
        actionContext,
      );
      await this.uninstallWidgetPlugin(context, fromSlot, actionContext);
      await documentServicesAPI.waitForChangesAppliedAsync();
    },
    async highlightSlot(slotPointer: SlotPointer) {
      resolveHostAPIBySlotPointer(slotPointer).highlightSlot(slotPointer);
    },
    unhighlightSlot(slotPointer: SlotPointer) {
      resolveHostAPIBySlotPointer(slotPointer).unhighlightSlot(slotPointer);
    },
    isWidgetSlotPopulated(slotPointer: SlotPointer): boolean {
      return Boolean(this.getSlotContentRef(slotPointer));
    },
    isPluginComponent(widgetPluginCompRef: CompRef): boolean {
      const componentType =
        documentServicesAPI.components.getType(widgetPluginCompRef);
      if (!SUPPORTED_PLUGIN_COMPONENT_TYPES.includes(componentType)) {
        return false;
      }

      return (
        tpaHostAPI.isPluginComponent(widgetPluginCompRef) ||
        blocksHostAPI.isPluginComponent(widgetPluginCompRef)
      );
    },
    async showOnPage(slotPointer: SlotPointer) {
      const placement = this.getWidgetSlotPlacement(slotPointer, {
        includePrivate: false,
      });

      if (
        !placement.appDefinitionId ||
        !placement.widgetId ||
        !placement.slotId
      ) {
        console.error(
          `Plugins Host API: 'showOnPage' missing placement params for ${placement}`,
        );
        return this.highlightSlot(slotPointer);
      }

      const hostComponentRef =
        resolveHostAPIBySlotPointer(
          slotPointer,
        ).getWidgetHostRefBySlotContainer(slotPointer);
      const appData = documentServicesAPI.platform.getAppDataByAppDefId(
        placement.appDefinitionId,
      );

      if (!appData?.applicationId) {
        console.error(
          `Plugins Host API: 'showOnPage' applicationId not found for ${placement}`,
        );
        return this.highlightSlot(slotPointer);
      }

      documentServicesAPI.platform.notifyApplication(
        appData?.applicationId,
        platformEvents.factory.widgetPluginShowOnPageClicked({
          hostComponentRef,
          placement: {
            appDefinitionId: placement.appDefinitionId,
            widgetId: placement.widgetId,
            slotId: placement.slotId,
          },
        }),
      );

      return this.highlightSlot(slotPointer);
    },
  };
};
